// src/services/user/UserServiceImpl.ts

import { notificationEndpoints, userEndpoints } from '@/api/endpoints';
import type { FetcherService } from '@/services/abstract/FetcherService';
import { UserService } from '@/services/abstract/UserService';
import type { User } from '@/types/UserState';
import type {
  NotificationsPayloadDTO,
  NotificationStatus,
  NotificationQueryParams,
  NotificationStatusDTO,
} from '@/types/NotificationDTOs';
import { buildQueryString } from '@/utils/buildQuery';
import type {
  CreateExternalUserPayload,
  CreateExternalUserResponse,
} from '@/types/create-six.types';
import type { ExternalUserDTO } from '@/types/external-users.types';

/**
 * Implementation of user service for managing current user profile
 * and notifications.
 *
 * Provides functionality for:
 * - Retrieving current user information
 * - Updating user profile details
 * - Fetching user notifications
 * - Marking notifications as read
 *
 * @see UserService
 */
export class UserServiceImpl extends UserService {
  /**
   * Creates an instance of UserServiceImpl
   * @param fetcher - Service for making HTTP requests to the backend
   */
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  /**
   * Retrieves the current authenticated user's profile information
   * @endpoint GET /private/v1/user/{id}
   * @returns User object containing current user's profile data
   * @throws UnauthorizedError if user is not authenticated
   * @tags user, profile
   */
  async getMe(id: string): Promise<User> {
    return await this.fetcher.get<User>(userEndpoints.getUser(id));
  }

  async getEncapsKey(id: string): Promise<{ encapsKey: string; mlkemKeyVersion: string }> {
    return await this.fetcher.get<{ encapsKey: string; mlkemKeyVersion: string }>(
      userEndpoints.getEncapsKey(id)
    );
  }

  async getDecapsKey(id: string): Promise<{ decapsKey: string }> {
    return await this.fetcher.get<{ decapsKey: string }>(userEndpoints.getDecapsKey(id));
  }

  /**
   * Retrieves the current user's notifications
   * @endpoint GET /private/v1/notifications
   * @param params - Optional query params (page, pageSize, read)
   * @returns NotificationsPayloadDTO containing notification items and unread count
   * @throws UnauthorizedError if user is not authenticated
   * @tags user, notifications
   */
  async getNotifications(params?: NotificationQueryParams): Promise<NotificationsPayloadDTO> {
    const url = `${notificationEndpoints.getNotifications}${buildQueryString(params !== undefined ? { ...params } : undefined)}`;
    return await this.fetcher.get<NotificationsPayloadDTO>(url);
  }

  /**
   * Marks a single notification as read
   * @endpoint POST /private/v1/notifications/{id}/mark-read
   * @param notificationId - ID of the notification to mark as read
   * @throws UnauthorizedError if user is not authenticated
   * @tags notifications
   */
  async markNotificationRead(
    notificationId: string,
    status: NotificationStatus
  ): Promise<NotificationStatusDTO> {
    return await this.fetcher.patch(notificationEndpoints.markRead(notificationId), { status });
  }

  async createNewExternalUser(
    payload: CreateExternalUserPayload
  ): Promise<CreateExternalUserResponse> {
    return await this.fetcher.post<CreateExternalUserResponse>(
      userEndpoints.createNewExternalUser,
      payload
    );
  }

  /**
   * Lists all external users on the account with SIX-participation counts
   * and creator attribution. Bare array — mirrors the gateway's
   * ExternalUserById view.
   * @endpoint GET /private/v1/users/external
   */
  async listExternalUsers(): Promise<ExternalUserDTO[]> {
    return await this.fetcher.get<ExternalUserDTO[]>(userEndpoints.listExternalUsers);
  }

  /**
   * Deletes an external user, freeing their seat. CQRS command — the gateway
   * responds 202 + commandId and the fetcher polls until the read store updates.
   * Deliberately no suppressPollingErrors: user-initiated, the modal must surface.
   * @endpoint DELETE /private/v1/users/external/{id}
   */
  async deleteExternalUser(id: string): Promise<void> {
    await this.fetcher.del(userEndpoints.deleteExternalUser(id));
  }
}
